import React, { useState } from 'react';

import { Box, Button, Divider, Stack, TextField } from '@mui/material';

import { Capacity, DEFAULT_CAPACITY } from '~/consts/capacities';

import ReservationService from '~/services/ReservationService';

import { formatValidationMessages } from '~/utils/formatValidationMessages';
import { reportError } from '~/utils/reportError';

import ErrorListDisplay from '../ErrorListDisplay';

import { buttonMessages, buttonStates } from './states/submitButton';

interface Props {
  onCancel: () => void;
  onRoomTypeIncludedGuestAdded: (guest: Capacity) => void;
  onRoomTypeIncludedGuestUpdated: (guest: Capacity) => void;
  includedGuest?: Capacity;
  propertyId: string;
  roomTypeId: string;
  roomRateId: string;
}

export default function RoomTypeIncludedGuestForm({
  onCancel,
  onRoomTypeIncludedGuestAdded,
  onRoomTypeIncludedGuestUpdated,
  includedGuest,
  propertyId,
  roomTypeId,
  roomRateId,
}: Props) {
  const [guest, setGuest] = useState<Capacity>(
    includedGuest ? JSON.parse(JSON.stringify(includedGuest)) : DEFAULT_CAPACITY,
  );

  const [saveState, setSaveState] = useState<string>(buttonStates.default);
  const [errors, setErrors] = useState<Record<string, Array<string>> | null>(null);

  const handleValueChange = (key: keyof Capacity, value: number) => {
    setGuest((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSubmit = async () => {
    try {
      setErrors(null);
      setSaveState(buttonStates.saving);

      const response = guest.id
        ? await ReservationService.updateRoomTypeIncludedGuest(guest, guest.id, propertyId, roomTypeId, roomRateId)
        : await ReservationService.createRoomTypeIncludedGuest(guest, propertyId, roomTypeId, roomRateId);

      setSaveState(buttonStates.saved);
      if (guest.id) {
        onRoomTypeIncludedGuestUpdated(response.result);
      } else {
        onRoomTypeIncludedGuestAdded(response.result);
      }
    } catch (error) {
      const validationErrors = error.name === 'ValidationError' ? error.errors : null;
      setSaveState(buttonStates.failed);
      setErrors(validationErrors);
      reportError(error);
    }
  };

  const formRow = (label: string, fieldName: keyof Capacity) => (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
      <Box sx={{ flex: 1 }}>{label}</Box>
      <Box sx={{ flex: 1 }}>
        <TextField
          type="number"
          inputProps={{ min: 0, step: 1 }}
          value={guest[fieldName]}
          onChange={(e) => handleValueChange(fieldName, parseInt(e.target.value) || 0)}
          fullWidth
          size="small"
        />
      </Box>
    </Box>
  );

  return (
    <Box sx={{ p: 1 }}>
      {formRow('Adults', 'adults')}
      {formRow('Children', 'children')}
      {formRow('Infants', 'infants')}
      {formRow('Teenagers', 'teenagers')}

      <Divider sx={{ my: 2 }} />

      {errors && (
        <Box sx={{ mb: 2 }}>
          <ErrorListDisplay messages={formatValidationMessages(errors)} />
        </Box>
      )}

      <Stack direction="row" justifyContent="space-between" spacing={2}>
        <Button variant="text" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="contained" type="submit" disabled={saveState === buttonStates.saving} onClick={handleSubmit}>
          {buttonMessages[saveState]}
        </Button>
      </Stack>
    </Box>
  );
}
